import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IcoFolder, IcoPlus, IcoClose } from '../dashboard/DashIcons';
import '../../styles/admin-dashboard.css';
 
function TeamDirectory() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, isManager, user } = useAuth();
  const [employees, setEmployees]     = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [searchTerm, setSearchTerm]   = useState('');
  const [expandedManagerId, setExpandedManagerId] = useState(null);
  const [showAllStaff, setShowAllStaff]   = useState(false);
  const [allStaffSearch, setAllStaffSearch] = useState('');
 
  const isAdminAssistant = user?.role === 'adminassistant';
  const canSeePay            = isSuperAdmin();
  const canViewManagerProfile = isSuperAdmin() || isAdminAssistant;
 
  // Superadmins and adminassistants see the manager-tree view.
  // Managers see their own employee list view.
  const showManagerView = isSuperAdmin() || isAdminAssistant;
 
  const getToken = () => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser).token : null;
  };
 
  const fetchEmployees = useCallback(async () => {
    try {
      const token = getToken();

      // Managers use the scoped endpoint that returns only their assigned employees.
      // Admins/superadmins use the full org endpoint.
      const isManagerRole = user?.role === 'manager';
      const endpoint = isManagerRole ? '/api/staff/department' : '/api/employees';
 
      const response = await fetch(endpoint, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
 
      if (response.ok) {
        const data = await response.json();
        // /api/employees returns { employees: [...] }
        // /api/staff/department returns a plain array
        const list = Array.isArray(data) ? data : data.employees || [];
        const filtered = isSuperAdmin()
          ? list
          : list.filter(emp => emp.role !== 'superadmin');
        setEmployees(filtered);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, user?.role]);
 
  const fetchDepartments = useCallback(async () => {
    try {
      const storedUser = localStorage.getItem('user');
      const userData   = storedUser ? JSON.parse(storedUser) : null;
      const token          = userData?.token;
      const organizationId = userData?.organizationId;
      const response = await fetch(`/api/departments?organizationId=${organizationId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setDepartments(await response.json());
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  }, []);
 
  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, [location.key, fetchEmployees, fetchDepartments]);
 
  const getDepartmentById = (deptId) => departments.find(d => d._id === deptId);
 
  // ── Admin/superadmin helpers ───────────────────────────────────────────────
  const managers = employees.filter(emp => emp.role === 'manager');
 
  const getEmployeesForManager = (managerId) =>
    employees.filter(emp =>
      emp.managerIds?.map(id => id.toString()).includes(managerId.toString()) &&
      emp.role !== 'manager' &&
      emp.role !== 'superadmin'
    );
 
  const filteredManagers = managers.filter(m =>
    `${m.firstName} ${m.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
 
  const unassignedEmployees = employees.filter(emp =>
    emp.role === 'employee' || emp.role === 'teamlead'
      ? (!emp.managerIds || emp.managerIds.length === 0)
      : false
  );

  const filteredUnassigned = unassignedEmployees.filter(emp =>
    `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ── Manager helpers ────────────────────────────────────────────────────────
  // When a manager fetches from /api/staff/department, the response is already
  // scoped to their team — no need to filter by managerIds again.
  const currentUserId = (user?._id || user?.id)?.toString();
 
  const myEmployees = user?.role === 'manager'
    ? employees  // already filtered server-side
    : employees.filter(emp =>
        emp.managerIds?.map(id => id.toString()).includes(currentUserId) &&
        emp.role !== 'manager' &&
        emp.role !== 'superadmin'
      );
 
  const filteredMyEmployees = myEmployees.filter(emp =>
    `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
 
  const allStaff = [...employees].sort((a, b) =>
    `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`)
  );
 
  const filteredAllStaff = allStaff.filter(emp =>
    `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(allStaffSearch.toLowerCase()) ||
    emp.email?.toLowerCase().includes(allStaffSearch.toLowerCase()) ||
    emp.role?.toLowerCase().includes(allStaffSearch.toLowerCase())
  );
 
  const handleManagerCardClick = (managerId) => {
    setExpandedManagerId(prev => prev === managerId ? null : managerId);
  };
 
  const handleEmployeeProfileClick = (e, employeeId) => {
    e.stopPropagation();
    navigate(`/employee/${employeeId}`);
  };
 
  const avatarStyle = (size = 50) => ({
    width: `${size}px`, height: `${size}px`, borderRadius: '50%',
    background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'white', fontSize: size > 40 ? '1.25rem' : '0.875rem',
    fontWeight: '700', flexShrink: 0,
  });
 
  const StatusBadge = ({ active }) => (
    <span title={active ? 'Active' : 'Inactive'} style={{ display: 'inline-flex', alignItems: 'center', cursor: 'default' }}>
      {active
        ? <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      }
    </span>
  );
 
  const roleBadgeStyle = (role) => {
    const map = {
      superadmin:     { bg: '#fef3c7', color: '#92400e' },
      admin:          { bg: '#fde8d8', color: '#9a3412' },
      adminassistant: { bg: '#e0f2fe', color: '#0369a1' },
      manager:        { bg: '#e0e7ff', color: '#4338ca' },
      employee:       { bg: '#f3f4f6', color: '#374151' },
    };
    const s = map[role] || map.employee;
    return { padding: '0.2rem 0.5rem', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '700', background: s.bg, color: s.color };
  };
 
  const roleDisplayName = (role) => {
    const map = { superadmin: 'Super Admin', admin: 'Admin', adminassistant: 'Admin Assistant', manager: 'Manager', employee: 'Employee' };
    return map[role] || role;
  };
 
  const EmployeeRow = ({ emp }) => (
    <div
      onClick={(e) => handleEmployeeProfileClick(e, emp._id)}
      style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.65rem 0.75rem', borderRadius: '8px', cursor: 'pointer', transition: 'background 0.15s', marginBottom: '0.25rem' }}
      onMouseEnter={e => e.currentTarget.style.background = '#f3f4f6'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <div style={avatarStyle(36)}>{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontWeight: '600', fontSize: '0.875rem', color: '#111827' }}>{emp.firstName} {emp.lastName}</p>
        <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280' }}>{emp.email}</p>
      </div>
      {emp.departmentIds?.length > 0 && (
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {emp.departmentIds.slice(0, 2).map(deptId => {
            const dept = getDepartmentById(deptId);
            if (!dept) return null;
            return <span key={deptId} style={{ padding: '0.2rem 0.5rem', background: dept.color, color: 'white', borderRadius: '4px', fontSize: '0.65rem', fontWeight: '600' }}>{dept.name}</span>;
          })}
        </div>
      )}
      <StatusBadge active={emp.active}/>
      <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>→</span>
    </div>
  );
 
  const ManagerCard = ({ manager }) => {
    const isExpanded  = expandedManagerId === manager._id;
    const teamMembers = getEmployeesForManager(manager._id);
    return (
      <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: `2px solid ${isExpanded ? '#22c55e' : 'transparent'}`, transition: 'all 0.2s', overflow: 'hidden' }}>
        <div
          onClick={() => handleManagerCardClick(manager._id)}
          style={{ padding: '1.5rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '1rem' }}
          onMouseEnter={e => { if (!isExpanded) { e.currentTarget.parentElement.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; e.currentTarget.parentElement.style.transform = 'translateY(-2px)'; } }}
          onMouseLeave={e => { e.currentTarget.parentElement.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; e.currentTarget.parentElement.style.transform = 'translateY(0)'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={avatarStyle(50)}>{manager.firstName?.[0]}{manager.lastName?.[0]}</div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>{manager.firstName} {manager.lastName}</h3>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.875rem', color: '#6b7280' }}>Manager</p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.35rem' }}>
              <span style={{ padding: '0.2rem 0.6rem', background: '#ede9fe', color: '#5b21b6', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '700' }}>
                {teamMembers.length} {teamMembers.length === 1 ? 'employee' : 'employees'}
              </span>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{isExpanded ? '▲ Hide team' : '▼ View team'}</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem', fontSize: '0.875rem', color: '#6b7280' }}>
            <div style={{ marginBottom: '0.35rem' }}>{manager.email || 'No email'}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {canSeePay && <div>${manager.hourlyRate?.toFixed(2) || '0.00'}/hr</div>}
              <StatusBadge active={manager.active}/>
            </div>
          </div>
        </div>
        {isExpanded && (
          <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.75rem 1rem 1rem', background: '#fafafa' }}>
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Team Members</p>
            {teamMembers.length === 0 ? (
              <p style={{ fontSize: '0.875rem', color: '#9ca3af', padding: '0.5rem 0.75rem' }}>No employees assigned yet.</p>
            ) : (
              teamMembers.map(emp => <EmployeeRow key={emp._id} emp={emp} />)
            )}
            {canViewManagerProfile && (
              <div
                onClick={(e) => handleEmployeeProfileClick(e, manager._id)}
                style={{ marginTop: '0.75rem', padding: '0.5rem 0.75rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', color: '#22c55e', fontWeight: '600', textAlign: 'center', border: '1px dashed #c4b5fd', transition: 'background 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#ede9fe'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                View Manager Profile →
              </div>
            )}
          </div>
        )}
      </div>
    );
  };
 
  const EmployeeCard = ({ employee }) => (
    <div
      onClick={() => navigate(`/employee/${employee._id}`)}
      style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'; e.currentTarget.style.borderColor = '#22c55e'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)'; e.currentTarget.style.borderColor = 'transparent'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        <div style={{ ...avatarStyle(50), marginRight: '1rem' }}>{employee.firstName?.[0]}{employee.lastName?.[0]}</div>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#111827' }}>
            {employee.firstName} {employee.lastName}
            {employee._id === currentUserId && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 6px', fontWeight: '700' }}>You</span>}
          </h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize' }}>{roleDisplayName(employee.role)}</p>
        </div>
      </div>
      <div style={{ paddingTop: '1rem', borderTop: '1px solid #e5e7eb', fontSize: '0.875rem', color: '#6b7280' }}>
        <div style={{ marginBottom: '0.5rem' }}>{employee.email || 'No email'}</div>
        {employee.departmentIds?.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
            {employee.departmentIds.slice(0, 3).map(deptId => {
              const dept = getDepartmentById(deptId);
              if (!dept) return null;
              return <span key={deptId} style={{ padding: '0.25rem 0.5rem', background: dept.color, color: 'white', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '600' }}>{dept.name}</span>;
            })}
            {employee.departmentIds.length > 3 && <span style={{ padding: '0.25rem 0.5rem', background: '#e5e7eb', color: '#6b7280', borderRadius: '4px', fontSize: '0.7rem', fontWeight: '600' }}>+{employee.departmentIds.length - 3}</span>}
          </div>
        )}
        <StatusBadge active={employee.active}/>
      </div>
    </div>
  );
 
  const SectionHeader = ({ emoji, title, count }) => (
    <h2 style={{ fontSize: '1rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1rem' }}>
      {emoji} {title} ({count})
    </h2>
  );
 
  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1>Team Directory</h1>
          <p className="subtitle">
            {isSuperAdmin()      ? 'View and manage your organization'
              : isAdminAssistant ? 'View managers and their assigned teams'
              : isManager()      ? 'View and manage your team members'
              : 'View your team members'}
          </p>
        </section>
 
        {/* Search + actions bar */}
        <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder={showManagerView ? 'Search...' : 'Search employees...'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: '260px', padding: '0.75rem 1rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '1rem' }}
          />
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {(isSuperAdmin() || isAdminAssistant || user?.role === 'manager') && (
              <button
                onClick={() => { setShowAllStaff(true); setAllStaffSearch(''); }}
                style={{ padding: '0.75rem 1.25rem', background: '#f0f4ff', color: '#4338ca', border: '2px solid #c7d2fe', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}
              >
                View All Staff
              </button>
            )}
            {(isSuperAdmin() || isManager() || isAdminAssistant) && (
              <>
                <button
                  onClick={() => navigate('/employee/import')}
                  style={{ padding: '0.75rem 1.25rem', background: '#f0fdf4', color: '#166534', border: '2px solid #bbf7d0', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}
                >
                  <><IcoFolder size={14}/> Import CSV</>
                </button>
                <button
                  onClick={() => navigate('/employee/new')}
                  style={{ padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}
                >
                  <><IcoPlus size={14}/> Add Employee</>
                </button>
              </>
            )}
          </div>
        </div>
 
        {/* Directory grid */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>Loading...</p>
          </div>
        ) : showManagerView ? (
          // ── Superadmin / adminassistant: manager tree view ──────────────────
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

            {/* My Team — only shown when the admin is also assigned as a manager */}
            {myEmployees.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    My Team ({myEmployees.length})
                  </h2>
                  <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>Staff directly assigned to you</span>
                </div>
                <div style={{ background: '#f0f4ff', borderRadius: '12px', border: '2px solid #c7d2fe', padding: '1rem 1.25rem' }}>
                  {filteredMyEmployees.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>No results for "{searchTerm}"</p>
                  ) : (
                    filteredMyEmployees.map(emp => <EmployeeRow key={emp._id} emp={emp} />)
                  )}
                </div>
              </div>
            )}

            <div>
              <SectionHeader emoji="" title="Managers" count={filteredManagers.length} />
              {filteredManagers.length === 0 ? (
                <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>{searchTerm ? 'No managers found.' : 'No managers yet.'}</p>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1.5rem' }}>
                  {filteredManagers.map(manager => <ManagerCard key={manager._id} manager={manager} />)}
                </div>
              )}
            </div>
 
            {(isSuperAdmin() || isAdminAssistant) && unassignedEmployees.length > 0 && (
              <div>
                <SectionHeader emoji="" title="Unassigned Staff" count={filteredUnassigned.length} />
                <div style={{ background: '#fff7ed', borderRadius: '12px', border: '2px solid #fed7aa', padding: '1rem 1.25rem' }}>
                  {filteredUnassigned.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '0.875rem', color: '#9ca3af' }}>No results for "{searchTerm}"</p>
                  ) : (
                    filteredUnassigned.map(emp => <EmployeeRow key={emp._id} emp={emp} />)
                  )}
                </div>
              </div>
            )}

            {isSuperAdmin() && (() => {
              const adminAssistants = employees.filter(emp =>
                emp.role === 'adminassistant' &&
                (!searchTerm || `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) || emp.email?.toLowerCase().includes(searchTerm.toLowerCase()))
              );
              return (
                <div>
                  <SectionHeader emoji="" title="Admin Assistants" count={adminAssistants.length} />
                  {adminAssistants.length === 0 ? (
                    <p style={{ color: '#9ca3af', fontSize: '0.9rem' }}>{searchTerm ? 'No admin assistants found.' : 'No admin assistants yet.'}</p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                      {adminAssistants.map(emp => <EmployeeCard key={emp._id} employee={emp} />)}
                    </div>
                  )}
                </div>
              );
            })()}
 
            {isSuperAdmin() && (() => {
              const admins = employees.filter(emp =>
                emp.role === 'admin' &&
                (!searchTerm || `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) || emp.email?.toLowerCase().includes(searchTerm.toLowerCase()))
              );
              if (!admins.length) return null;
              return (
                <div>
                  <SectionHeader emoji="" title="Admins" count={admins.length} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {admins.map(emp => <EmployeeCard key={emp._id} employee={emp} />)}
                  </div>
                </div>
              );
            })()}

            {isSuperAdmin() && (() => {
              const superAdmins = employees.filter(emp =>
                emp.role === 'superadmin' &&
                (!searchTerm || `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) || emp.email?.toLowerCase().includes(searchTerm.toLowerCase()))
              );
              if (!superAdmins.length) return null;
              return (
                <div>
                  <SectionHeader emoji="" title="Super Admins" count={superAdmins.length} />
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {superAdmins.map(emp => <EmployeeCard key={emp._id} employee={emp} />)}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          // ── Manager: their own team view ────────────────────────────────────
          filteredMyEmployees.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <p style={{ fontSize: '1.25rem', color: '#6b7280' }}>
                {searchTerm ? 'No employees found matching your search.' : 'No employees assigned to you yet.'}
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
              {filteredMyEmployees.map(emp => <EmployeeCard key={emp._id} employee={emp} />)}
            </div>
          )
        )}
      </div>
 
      {/* All Staff Modal — superadmin/adminassistant only */}
      {showAllStaff && (
        <>
          <div onClick={() => setShowAllStaff(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: 'white', borderRadius: '16px', width: '90%', maxWidth: '700px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', zIndex: 1001, overflow: 'hidden' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800', color: '#111827' }}>All Staff</h2>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.78rem', color: '#9ca3af' }}>{allStaff.length} total · click any name to view profile</p>
              </div>
              <button onClick={() => setShowAllStaff(false)} style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '0.5rem 0.75rem', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}><IcoClose size={16}/></button>
            </div>
            <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid #f3f4f6' }}>
              <input type="text" placeholder="Search by name, email, or role..." value={allStaffSearch} onChange={e => setAllStaffSearch(e.target.value)} autoFocus
                style={{ width: '100%', padding: '0.65rem 1rem', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = '#22c55e'}
                onBlur={e => e.target.style.borderColor = '#e5e7eb'}
              />
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '0.75rem 1rem' }}>
              {filteredAllStaff.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem' }}>No staff found matching your search.</p>
              ) : filteredAllStaff.map(emp => (
                <div key={emp._id} onClick={() => { setShowAllStaff(false); navigate(`/employee/${emp._id}`); }}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.75rem', borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s', marginBottom: '0.25rem' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={avatarStyle(42)}>{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>
                      {emp.firstName} {emp.lastName}
                      {emp._id === currentUserId && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', background: '#fef3c7', color: '#92400e', borderRadius: '4px', padding: '1px 6px', fontWeight: '700' }}>You</span>}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#9ca3af' }}>{emp.email}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={roleBadgeStyle(emp.role)}>{roleDisplayName(emp.role)}</span>
                    <StatusBadge active={emp.active}/>
                    <span style={{ fontSize: '0.75rem', color: '#d1d5db' }}>→</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
 
export default TeamDirectory